import React = require("react");
import l10n from "../util/l10n";
import { FeedbackType } from './FeedbackButton';
import Mask from "../components/Mask";
import Delayed from '../components/Delayed';
import Dialog from "material-ui/Dialog";
import TextField from "../custom-components/TextField/TextField";
import FlatButton from "material-ui/FlatButton";
import RaisedButton from "material-ui/RaisedButton";
import CircularProgress from 'material-ui/CircularProgress';
import { colors } from 'material-ui/styles';
import * as features from '../util/features';
import diyLightTheme = require('../material-ui/diyLightTheme');
import { autoprefixer } from '../material-ui/autoprefixer';
import {Notice as PrivacyNotice} from './../privacy/Notice';

import SmileIcon from "material-ui/svg-icons/social/mood";
import SadIcon from "material-ui/svg-icons/social/mood-bad";
import CheckCircle from "material-ui/svg-icons/action/check-circle";

const DYN_STYLE_ID = 'feedback-form-dialog-dynamic-style';

interface FeedbackFormDialogProps extends React.Props<FeedbackFormDialog> {
    feedbackFeatureName: string;
    feedbackType: FeedbackType;
    feedbackContext: string;
    onFeedbackSubmit(
        feedbackContext: string,
        feedbackType: FeedbackType,
        feedbackText: string
        ): Promise<string>;
    onDone(): void;
}

interface FeedbackFormDialogState {
    isWaiting: boolean;
    isSuccess: boolean;
    isFading: boolean;
    submitBtnDisabled: boolean;
    message: string;
}

export default class FeedbackFormDialog extends React.Component<FeedbackFormDialogProps, FeedbackFormDialogState> {

    constructor(props: FeedbackFormDialogProps) {
         super(props);
         this.state = {
             isWaiting: false,
             isSuccess: false,
             isFading: false,
             submitBtnDisabled: true,
             message: ''
         };
     }

    // https://issue.tool.1and1.com/browse/DHDIYRED-1547
    // {
    componentDidMount() {
        // http://stackoverflow.com/a/524721

        const css = 'html { overflow-y: visible; }';
        const head = document.head || document.getElementsByTagName('head')[0];
        const style = document.createElement('style');

        style.id = DYN_STYLE_ID;
        style.type = 'text/css';

        if ((style as any).styleSheet) {
            (style as any).styleSheet.cssText = css;
        } else {
            style.appendChild(document.createTextNode(css));
        }

        head.appendChild(style);
    }

    componentWillUnmount() {
        const style = document.getElementById(DYN_STYLE_ID);

        style.parentElement.removeChild(style);
    }
    // }

    getStyles() {
        return {
            dialog: {
                 opacity: 1,
            },
            dialogContent: {
                maxWidth: 505,
                marginBottom: 64
            },
            dialogBody: {
                padding: "24px 24px 0px 24px"
            },
            actions: {
                paddingRight: 24,
                visibility: "visible"
            },
            title: autoprefixer({
                fontWeight: 500,
                fontSize: 16,
                display: "flex"
            }),
            titleText: {
                padding: "0px 5px 0px 5px"
            },
            moduleName: {
                fontWeight: 700
            },
            paragraph: autoprefixer({
                fontSize: 13,
                margin: "24px 0px",
                display: "flex"
            }),
            paragraphNote: autoprefixer({
                fontSize: 12,
                margin: "24px 0px",
                display: "flex",
                color: "rgba(0,0,0,.9)"
            }),
            success: autoprefixer({
                display: "flex",
                flexDirection: "column",
                width: 425
            }),
            successText: autoprefixer({
                display: "flex",
                justifyContent: "center"
            }),
            checkCircleContainer: autoprefixer({
                display: "flex",
                justifyContent: "center",
                paddingBottom: 20
            }),
            checkCircle: {
                width: 60,
                height: 60
            },
            fading: {
                opacity: 0,
                transition: "opacity 0.5s ease-in-out"
            },
        };
    };

    render() {
        let styles = this.getStyles();

        const formTitlePositive = l10n("feedback.form.title.positive");
        const formTitleNegative = l10n("feedback.form.title.negative");

        const formBody = l10n("feedback.form.body");
        const formQuestion = l10n("feedback.form.question");
        const formNote = l10n("feedback.form.note");

        const btnCancelText = l10n("feedback.form.button.cancel");
        const btnSubmitText = l10n("feedback.form.button.submit");

        const successText = l10n("feedback.form.success");

        const actions = [
             <FlatButton
                style={styles}
                label={btnCancelText}
                primary={true}
                onTouchTap={this.btnCancelAction}
                data-test-id="feedbackform-cancel-btn"
             />,
             <RaisedButton
                disabled={this.state.submitBtnDisabled}
                label={btnSubmitText}
                primary={true}
                onTouchTap={this.btnSubmitAction}
                data-test-id="feedbackform-submit-btn"
             />
         ];

        return (
            <Dialog
                actionsContainerStyle={styles.actions}
                actions={actions}
                modal={true}
                open={true}
                style={this.state.isFading ? styles.fading : styles.dialog}
                contentStyle={styles.dialogContent}
                bodyStyle={styles.dialogBody}
                >
                <div style={styles.title}>
                    {
                        this.props.feedbackType === FeedbackType.POSITIVE ? <SmileIcon /> : <SadIcon />
                    }
                    <p style={styles.titleText}>
                    {this.props.feedbackType === FeedbackType.POSITIVE ? formTitlePositive : formTitleNegative}
                    </p>
                    <p style={styles.moduleName}>
                        {this.props.feedbackFeatureName}
                    </p>
                </div>
                <div style={styles.paragraph}>
                    {formBody}
                </div>
                <div style={styles.paragraph}>
                    {formQuestion}
                </div>
                <div style={styles.paragraph}>
                        <TextField
                            data-test-id="feedbackform-textarea"
                            focusOnMount={true}
                            onChange={this.onTextFieldChange}
                            multiLine={true}
                            rows={5}
                            rowsMax={10}
                            name="feedbackform-textarea"
                        />
                </div>
                <div style={styles.paragraphNote}>
                    {formNote}
                </div>
                {
                    features('privacyNotice')
                    ?
                    <div style={styles.paragraphNote}>
                        <PrivacyNotice />
                    </div>
                    :
                    null
                }
                {
                    this.state.isWaiting
                    ?
                    <Delayed delay={300}>
                        <Mask backgroundColor={diyLightTheme.paper.backgroundColor}>
                            <CircularProgress mode="indeterminate" />
                        </Mask>
                    </Delayed>
                    :
                    null
                }
                {
                    this.state.isSuccess
                    ?
                    <div>
                        <Mask backgroundColor={diyLightTheme.paper.backgroundColor}>
                            <div style={styles.success}>
                                <div style={styles.checkCircleContainer}>
                                    <CheckCircle style={styles.checkCircle} color={colors.blue500}/>
                                </div>
                                <div style={styles.successText}>
                                    {successText}
                                </div>
                            </div>
                        </Mask>
                    </div>
                    :
                    null
                }
            </Dialog>
        );
    }

    private onTextFieldChange = (event: React.SyntheticEvent<{}>) => {
        const message = (event.target as HTMLTextAreaElement).value.trim();
        const newState = {} as FeedbackFormDialogState;
        newState.submitBtnDisabled = message.length === 0;
        newState.message = message;
        this.setState(newState);
    };

    private btnCancelAction = () => {
        this.props.onDone();
    };

    private btnSubmitAction = () => {

        const newState = {} as FeedbackFormDialogState;
        newState.isWaiting = true;
        this.setState(newState);

        const promise = this.props.onFeedbackSubmit(
            this.props.feedbackContext,
            this.props.feedbackType,
            this.state.message
        );

        promise.then(() => {
            const newState = {} as FeedbackFormDialogState;
            newState.isWaiting = false;
            newState.isSuccess = true;
            this.setState(newState);

            setTimeout(() => {
                const newState = {} as FeedbackFormDialogState;
                newState.isFading = true;
                this.setState(newState);
                // after same duration as the fading animation close the dialog
                setTimeout(() => {
                    this.props.onDone();
                }, 500);
            }, 2000);
        }).catch(() => {
            // in case of error
            const newState = {} as FeedbackFormDialogState;
            newState.isWaiting = false;
            this.setState(newState);
        });
    };
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/feedback/FeedbackFormDialog.tsx
 **/